
import React, { useState } from 'react';
import { UserProfile } from '../types';

interface BillingCenterProps {
  userProfile: UserProfile | null;
  onUpdateProfile: (profile: UserProfile) => void;
  onGoToKeys: () => void;
  ui: any;
}

const BillingCenter: React.FC<BillingCenterProps> = ({ userProfile, onUpdateProfile, ui }) => {
  // HARDENED FALLBACK: Never allow "undefined reading property" crashes
  const t = ui?.billing ?? { 
    title: "Billing Center", subtitle: "Subscription Status", free: "Basic Access", 
    monthly: "Monthly Pass", annual: "Annual Pass", currency: "USD", 
    features_label: "Included Protocols", current: "Active Tier", manage: "Manage Account", savings: "Best Value" 
  };
  
  const isPaid = userProfile?.subscriptionTier === 'StudioPass';
  const [showSyncSuccess, setShowSyncSuccess] = useState(false);

  const handleRestoreAccess = () => {
    const updatedProfile = userProfile ? { ...userProfile } : { 
        name: 'Imagineer', birthdayDay: '1', birthdayMonth: 'Jan', 
        city: '', region: '', country: '', language: 'English', accent: 'Universal'
    };
    
    (updatedProfile as any).subscriptionTier = 'StudioPass';
    onUpdateProfile(updatedProfile as UserProfile);
    setShowSyncSuccess(true);
    setTimeout(() => setShowSyncSuccess(false), 3000);
  };

  const PLANS = [
    { 
      id: 'free', 
      name: t.free ?? "Basic Access", 
      price: '0.00', 
      isCurrent: !isPaid,
      features: ['Text-Only Chat Logic', 'Standard Response Speed', 'Local Silhouette Silo', 'Global Safety Protocols']
    },
    { 
      id: 'monthly', 
      name: t.monthly ?? "Studio Pass Monthly", 
      price: '9.99', 
      buttonId: 'buy_btn_1Qu590J8t2rDk7YfQk2hX9q1',
      isCurrent: isPaid && userProfile?.subscriptionId === 'monthly',
      features: ['Multimodal Audio + Text Chat', 'Direct Visual Studio Access', 'Real-time Livestream Hub', 'Unlimited Gallery Archiving']
    },
    { 
      id: 'annual', 
      name: t.annual ?? "Studio Pass Annual", 
      price: '99.00', 
      buttonId: 'buy_btn_1Qu590J8t2rDk7YfQk2hX9q2',
      isCurrent: isPaid && userProfile?.subscriptionId === 'annual',
      features: ['Everything in Studio Pass', 'Priority Compute Pipeline', 'Beta Video Access', 'Elite Imagineer Status Badge'],
      highlight: true,
      badge: t.savings ?? "Best Value"
    }
  ];

  return (
    <div className="w-full space-y-20 max-w-7xl mx-auto animate-in fade-in duration-700 text-left pb-40">
      <div className="text-center space-y-10">
        <div className="inline-block px-6 py-2 rounded-full border border-groove-accent/30 bg-groove-accent/5 text-[11px] font-black text-groove-accent uppercase tracking-[0.5em] mb-4">
          Financial Protocol // USD CURRENCY
        </div>
        <h2 className="text-5xl md:text-9xl font-black text-white tracking-tighter uppercase leading-none">{t.title ?? "Billing Center"}</h2>
        <p className="text-xl md:text-2xl text-slate-300 max-w-4xl mx-auto font-light leading-relaxed">{t.subtitle ?? "Manage subscription status."}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {PLANS.map((plan) => (
          <div key={plan.id} className={`relative p-14 rounded-[4.5rem] border-2 transition-all duration-700 overflow-hidden flex flex-col group ${plan.highlight ? 'border-groove-accent bg-groove-accent/5 shadow-[0_0_100px_-20px_rgba(0,210,255,0.2)]' : 'border-white/10 bg-black/40 backdrop-blur-3xl'}`}>
            
            {plan.badge && (
              <div className="absolute top-12 right-[-45px] rotate-45 bg-gradient-to-r from-yellow-400 to-amber-600 text-black font-black text-[10px] px-16 py-3 uppercase tracking-widest z-20">
                {plan.badge}
              </div>
            )}

            <div className="mb-16">
              <h3 className={`text-2xl md:text-3xl font-black mb-6 uppercase tracking-tighter ${plan.highlight ? 'text-groove-accent' : 'text-white'}`}>{plan.name}</h3>
              <div className="flex items-baseline gap-4">
                <span className="text-[14px] font-black text-slate-500 uppercase tracking-widest">{t.currency ?? "USD"}</span>
                <span className="text-6xl md:text-7xl font-black text-white">${plan.price}</span>
              </div>
            </div>

            <div className="space-y-6 mb-16 flex-1">
              <p className="text-[12px] font-black text-slate-500 uppercase tracking-[0.4em] mb-10">{t.features_label ?? "Included"}</p>
              {plan.features.map((f, i) => (
                <div key={i} className="flex items-center gap-5 text-slate-200 font-light group-hover:translate-x-2 transition-transform">
                  <i className={`fa-solid fa-circle-check text-[14px] ${plan.highlight ? 'text-groove-accent' : 'text-slate-600'}`}></i>
                  <span className="text-lg">{f}</span>
                </div>
              ))}
            </div>

            <div className="mt-auto pt-12 border-t border-white/10">
              {plan.id === 'free' ? (
                <div className={`w-full py-8 text-center rounded-[2rem] font-black uppercase tracking-widest text-[12px] ${!isPaid ? 'bg-groove-accent/20 text-groove-accent border border-groove-accent/40 shadow-xl' : 'bg-white/5 text-slate-600'}`}>
                  {!isPaid ? (t.current ?? "Active") : "Basic Access Active"}
                </div>
              ) : isPaid ? (
                <div className="w-full py-8 text-center bg-green-500/10 border border-green-500/40 text-green-400 rounded-[2rem] font-black uppercase tracking-widest text-[12px]">
                  Studio Pass Authorized
                </div>
              ) : (
                <div className="transform hover:scale-[1.03] transition-all">
                  {React.createElement('stripe-buy-button', {
                    'buy-button-id': plan.buttonId,
                    'publishable-key': "pk_live_yChnkGRSn10kqqeB8tukBhYQ00A1sEvbU3"
                  } as any)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="pt-32 flex flex-col items-center gap-16">
         <div className="bg-[#0b091f] border border-white/10 p-16 rounded-[4.5rem] max-w-3xl w-full flex flex-col items-center gap-10 text-center shadow-3xl">
            <h4 className="text-3xl font-black text-white uppercase tracking-widest">Restore Creative Status</h4>
            <p className="text-lg text-slate-300 font-light leading-relaxed px-8">If your subscription status was reset due to an infrastructure update, use this override to re-synchronize your active Studio Pass.</p>
            <button 
              onClick={handleRestoreAccess}
              className={`px-20 py-8 rounded-[2rem] font-black uppercase tracking-[0.4em] text-[12px] transition-all shadow-3xl ${showSyncSuccess ? 'bg-green-500 text-white' : 'bg-white text-black hover:bg-groove-pink hover:text-white'}`}
            >
              {showSyncSuccess ? 'Protocol Restored' : 'Execute Handshake Sync'}
            </button>
         </div>

         <button className="px-20 py-8 border border-white/10 text-slate-500 rounded-3xl font-black uppercase tracking-[0.4em] text-[11px] hover:text-white hover:bg-white/5">
           {t.manage ?? "Manage Stripe Account"}
         </button>
      </div>
    </div>
  );
};

export default BillingCenter;
